// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { useCoinMetadata } from '@mysten/core';
import { Unstaked } from '@safecoin/icons';
import { SAFE_TYPE_ARG } from '@safecoin/safe/utils';
import { cva, type VariantProps } from 'class-variance-authority';

import { ImageIcon } from '~/ui/ImageIcon';
import { SafeCoinLogo } from '~/ui/SafeCoinLogo';

const imageStyle = cva(['flex rounded-2xl'], {
	variants: {
		size: {
			sm: 'w-6 h-6',
			md: 'w-7.5 h-7.5',
			lg: 'md:w-10 md:h-10 w-8 h-8',
			xl: 'md:w-31.5 md:h-31.5 w-16 h-16 ',
		},
	},
	defaultVariants: {
		size: 'md',
	},
});

function SafeGlyph() {
	return <SafeCoinLogo className="h-full w-full rounded-2xl" />;
}

type CoinImageProps = {
	coinType: string;
	size: VariantProps<typeof imageStyle>['size'];
};

// The on-chain CoinMetadata icon wins for every coin, including the native one —
// SAFE publishes an iconUrl, and the built-in glyph is only the fallback.
function CoinImage({ coinType, size }: CoinImageProps) {
	const { data: coinMeta } = useCoinMetadata(coinType);
	const isSafe = coinType === SAFE_TYPE_ARG;

	if (coinMeta?.iconUrl) {
		return (
			<ImageIcon
				size={size}
				src={coinMeta.iconUrl}
				label={coinMeta.name || coinType}
				fallback={coinMeta.name || coinType}
				circle
			/>
		);
	}

	if (isSafe) {
		return <SafeGlyph />;
	}

	return (
		<div className="flex h-full w-full items-center justify-center rounded-2xl bg-gray-40 text-hero-darkest text-opacity-30">
			<div className="flex h-full w-full items-center justify-center rounded-2xl border-2 border-hero-darkest border-opacity-10">
				<Unstaked className="h-2.5 w-2.5" />
			</div>
		</div>
	);
}

export interface CoinIconProps extends VariantProps<typeof imageStyle> {
	coinType: string;
}

export function CoinIcon({ coinType, ...styleProps }: CoinIconProps) {
	return (
		<div className={imageStyle(styleProps)}>
			<CoinImage coinType={coinType} size={styleProps.size} />
		</div>
	);
}
